import { useEffect, useState } from 'preact/hooks'
import { READINGS_PER_SITE } from '~/calculations/measurementQuality'
import { MeasurementHistory } from '~/components/feedback/MeasurementHistory'
import { MeasurementResult } from '~/components/feedback/MeasurementResult'
import { Button } from '~/components/primitives/Button'
import { NumericField } from '~/components/primitives/Field'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import {
  MEASUREMENT_SITES,
  type MeasurementSiteKey,
  SITE_CATEGORY,
  SITE_LABELS,
} from '~/models/measurementSite'
import type { SettingsRecord } from '~/models/settings'
import {
  type MeasurementInput,
  measurementErrorMessage,
  saveMeasurementSession,
} from '~/services/measurementService'
import { todayFor } from '~/services/weightService'
import { useDatabase } from '~/storage/DatabaseContext'
import { deleteMeasurementSession } from '~/storage/repositories/measurementRepository'
import type { MeasurementSessionId } from '~/types/ids'
import styles from './ReviewPage.module.css'

/**
 * The weekly measurement capture, unchanged in behaviour since Checkpoint 1.4.
 *
 * It became its own component at Checkpoint 2.2, when it moved inside the
 * wizard and the page around it grew a step list, a draft and a finalize. It
 * owns its own entry state and reports only whether it currently holds unsaved
 * readings, which is all the wizard needs in order to warn about them.
 *
 * Every recorded site takes exactly three readings (D-045). Recommended and
 * optional sites can be left blank to skip them entirely; there is no partial
 * state.
 */

type SiteDraft = Readonly<Record<MeasurementSiteKey, readonly string[]>>

function emptyDraft(): SiteDraft {
  // Built by filling every key rather than cast from `Object.fromEntries`,
  // whose index-signature result would not actually prove the record complete.
  const draft = {} as Record<MeasurementSiteKey, readonly string[]>
  for (const site of MEASUREMENT_SITES) draft[site] = ['', '', '']
  return draft
}

const EMPTY_DRAFT = emptyDraft()

interface MeasurementCaptureProps {
  settings: SettingsRecord
  latest: MeasurementSessionRecord | undefined
  sessions: readonly MeasurementSessionRecord[]
  onSaved: () => void
  /** Reports unsaved readings so the wizard can warn before moving on. */
  onPendingChange: (pending: boolean) => void
}

export function MeasurementCapture({
  settings,
  latest,
  sessions,
  onSaved,
  onPendingChange,
}: MeasurementCaptureProps) {
  const db = useDatabase()
  const [draft, setDraft] = useState<SiteDraft>(EMPTY_DRAFT)
  const [errors, setErrors] = useState<readonly string[]>([])
  const [saving, setSaving] = useState(false)
  const [showOptional, setShowOptional] = useState(false)
  const [deleting, setDeleting] = useState<MeasurementSessionId | undefined>(undefined)

  const hasUnsavedReadings = MEASUREMENT_SITES.some((site) =>
    (draft[site] ?? []).some((reading) => reading.trim() !== ''),
  )

  useEffect(() => {
    onPendingChange(hasUnsavedReadings)
  }, [hasUnsavedReadings, onPendingChange])

  function setReading(site: MeasurementSiteKey, index: number, value: string) {
    setDraft((current) => {
      const next = [...(current[site] ?? ['', '', ''])]
      next[index] = value
      return { ...current, [site]: next }
    })
    setErrors([])
  }

  async function removeSession(id: MeasurementSessionId) {
    setDeleting(id)
    const removed = await deleteMeasurementSession(db, id)
    setDeleting(undefined)

    if (!removed.ok) {
      setErrors([removed.error.message])
      return
    }
    setErrors([])
    onSaved()
  }

  async function save() {
    // A site counts as recorded when any of its boxes has content.
    //
    // Blank boxes are dropped rather than sent as NaN, so a half-filled site
    // arrives with fewer than three readings and gets the useful error ("enter
    // all three, or leave it blank to skip") instead of the misleading one
    // ("readings must be numbers"). A box containing actual garbage still
    // becomes NaN and is reported as such.
    const sites: Record<string, number[]> = {}
    for (const site of MEASUREMENT_SITES) {
      const entered = (draft[site] ?? [])
        .map((value) => value.trim())
        .filter((value) => value !== '')
      if (entered.length === 0) continue
      sites[site] = entered.map((value) => Number(value))
    }

    setSaving(true)
    const result = await saveMeasurementSession(
      db,
      { date: todayFor(new Date()), sites: sites as MeasurementInput['sites'] },
      new Date(),
    )
    setSaving(false)

    if (!result.ok) {
      setErrors(measurementErrorMessage(result.error).split('. ').filter(Boolean))
      return
    }
    /* No update signal here. Saving measurements is one step of a review that is
       still open, and reporting it as finalized would offer an update in the
       middle of the wizard — precisely what D-044 rule 1 forbids. The wizard
       releases the deferral when the review is finished or left. */
    setDraft(EMPTY_DRAFT)
    setErrors([])
    onSaved()
  }

  const optionalSites = MEASUREMENT_SITES.filter((site) => SITE_CATEGORY[site] !== 'required')

  return (
    <>
      {latest === undefined ? null : (
        <MeasurementResult
          derived={latest.derived}
          sites={latest.sites}
          unit={settings.weightDisplayUnit}
        />
      )}

      <section aria-labelledby="conditions-heading">
        <h2 id="conditions-heading" class={styles.sectionHeading}>
          Before you measure
        </h2>
        <ul class={styles.conditions}>
          <li>Measure in the morning, after using the bathroom.</li>
          <li>Measure before food, drink, or training.</li>
          <li>Use the same flexible, non-stretch tape each week.</li>
          <li>Stand naturally. Keep the tape level and snug, without compressing the skin.</li>
          <li>Take three readings for each site you record.</li>
        </ul>
        {/* B-003 is unresolved: the exact neck landmark and tape angle, and the
            landmarks for the other sites, are not yet decided. Inventing them
            here would undermine the repeatability the whole estimate rests on,
            so the gap is stated instead. */}
        <p class={styles.pending}>
          Detailed guidance for each landmark — where exactly to place the tape on the neck and the
          other sites — has not been finalised yet. Until it is, measure each site the same way
          every week and note how you did it.
        </p>
      </section>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
      >
        <SiteGroup
          heading="Required"
          sites={MEASUREMENT_SITES.filter((site) => SITE_CATEGORY[site] === 'required')}
          draft={draft}
          onChange={setReading}
        />

        <button
          type="button"
          class={styles.toggle}
          aria-expanded={showOptional}
          onClick={() => setShowOptional((value) => !value)}
        >
          {showOptional ? 'Hide other sites' : 'Add other sites'}
        </button>

        {showOptional ? (
          <SiteGroup
            heading="Recommended and optional"
            hint="Leave a site completely blank to skip it. If you record one, enter all three readings."
            sites={optionalSites}
            draft={draft}
            onChange={setReading}
          />
        ) : null}

        <p class={styles.thresholdNote}>
          Project Sola saves the middle reading of the three and warns when they vary by more than{' '}
          {settings.circumferenceSpreadThresholdCm} cm.
        </p>

        {errors.length > 0 ? (
          <div class={styles.errors} role="alert">
            {errors.map((message) => (
              <p key={message}>{message.endsWith('.') ? message : `${message}.`}</p>
            ))}
          </div>
        ) : null}

        <Button type="submit" busy={saving} fullWidth>
          Save measurements
        </Button>
      </form>

      {/* Below the capture form: reviewing what is already saved is a
          correction task, not part of taking this week's measurement. */}
      <MeasurementHistory
        sessions={sessions}
        lengthUnit={settings.lengthDisplayUnit}
        onDelete={(id) => void removeSession(id)}
        busyId={deleting}
      />
    </>
  )
}

interface SiteGroupProps {
  heading: string
  hint?: string
  sites: readonly MeasurementSiteKey[]
  draft: SiteDraft
  onChange: (site: MeasurementSiteKey, index: number, value: string) => void
}

function SiteGroup({ heading, hint, sites, draft, onChange }: SiteGroupProps) {
  const headingId = `group-${heading.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} class={styles.sectionHeading}>
        {heading}
      </h2>
      {hint === undefined ? null : <p class={styles.groupHint}>{hint}</p>}

      {sites.map((site) => (
        <fieldset key={site} class={styles.site}>
          <legend class={styles.siteLabel}>{SITE_LABELS[site]}</legend>
          <div class={styles.readings}>
            {Array.from({ length: READINGS_PER_SITE }, (_, index) => (
              <NumericField
                key={`${site}-${index}`}
                label={`${SITE_LABELS[site]} reading ${index + 1}`}
                suffix="cm"
                value={draft[site]?.[index] ?? ''}
                onInput={(value) => onChange(site, index, value)}
              />
            ))}
          </div>
        </fieldset>
      ))}
    </section>
  )
}
